import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManageChatLogs = () => {
  const [sessions, setSessions] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedSession, setSelectedSession] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('adminToken');
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const [sessionsRes, feedbackRes, questionsRes, statsRes] = await Promise.all([
        axios.get(`${API}/chat/admin/sessions?limit=100`, { headers }),
        axios.get(`${API}/chat/admin/feedback?limit=100`, { headers }),
        axios.get(`${API}/chat/admin/questions?limit=100`, { headers }),
        axios.get(`${API}/chat/admin/stats`, { headers })
      ]);
      
      setSessions(sessionsRes.data.sessions || []);
      setFeedback(feedbackRes.data.feedback || []);
      setQuestions(questionsRes.data.questions || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch chat data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateFeedbackStatus = async (feedbackId, status) => {
    const token = localStorage.getItem('adminToken');
    try {
      await axios.put(`${API}/chat/admin/feedback/${feedbackId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Failed to update feedback status:', error);
    }
  };

  const markQuestionAnswered = async (questionId) => {
    const token = localStorage.getItem('adminToken');
    try {
      await axios.put(`${API}/chat/admin/questions/${questionId}/answered?answered=true`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Failed to mark question answered:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'converted': return 'bg-green-100 text-green-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      case 'new': return 'bg-yellow-100 text-yellow-800';
      case 'reviewed': return 'bg-purple-100 text-purple-800';
      case 'responded': return 'bg-teal-100 text-teal-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getLeadTypeIcon = (type) => {
    switch (type) {
      case 'appointment': return '📅';
      case 'feedback': return '💬';
      case 'question': return '❓';
      case 'review': return '⭐';
      default: return '👤';
    }
  };

  const getFeedbackTypeColor = (type) => {
    switch (type) {
      case 'praise': return 'bg-green-100 text-green-800';
      case 'suggestions': return 'bg-blue-100 text-blue-800';
      case 'comments': return 'bg-gray-100 text-gray-800';
      case 'questions': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredSessions = sessions.filter(s => {
    if (filterStatus === 'all') return true;
    return s.status === filterStatus;
  });

  if (loading) {
    return <div className="p-6">Loading chat logs...</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Chat Widget Logs</h1>
          <p className="text-gray-500">View chat sessions, feedback, and questions from the chat widget</p>
        </div>
        <Button onClick={fetchData} variant="outline">🔄 Refresh</Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-gray-800">{stats.sessions?.total || 0}</div>
              <div className="text-sm text-gray-500">Total Sessions</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-green-600">{stats.sessions?.converted || 0}</div>
              <div className="text-sm text-gray-500">Converted</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-blue-600">{stats.leads?.appointments || 0}</div>
              <div className="text-sm text-gray-500">Appointments</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-purple-600">{stats.feedback?.total || 0}</div>
              <div className="text-sm text-gray-500">Feedback</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-yellow-600">{stats.feedback?.new || 0}</div>
              <div className="text-sm text-gray-500">New Feedback</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-red-600">{stats.questions?.unanswered || 0}</div>
              <div className="text-sm text-gray-500">Unanswered Q's</div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="sessions">Sessions ({sessions.length})</TabsTrigger>
          <TabsTrigger value="feedback">Feedback ({feedback.length})</TabsTrigger>
          <TabsTrigger value="questions">Questions ({questions.length})</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Leads */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">🎯 Recent Leads</CardTitle>
              </CardHeader>
              <CardContent>
                {sessions.filter(s => s.status === 'converted').length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No converted leads yet</p>
                ) : (
                  <div className="space-y-3">
                    {sessions.filter(s => s.status === 'converted').slice(0, 5).map(session => (
                      <div key={session.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{getLeadTypeIcon(session.lead_type)}</span>
                          <div>
                            <p className="font-medium text-gray-800">{session.visitor_name || 'Anonymous'}</p>
                            <p className="text-xs text-gray-500">{session.visitor_email || session.visitor_phone || 'No contact'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={getStatusColor(session.lead_type)}>{session.lead_type}</Badge>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(session.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* New Feedback */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">💬 New Feedback</CardTitle>
              </CardHeader>
              <CardContent>
                {feedback.filter(f => f.status === 'new').length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No new feedback</p>
                ) : (
                  <div className="space-y-3">
                    {feedback.filter(f => f.status === 'new').slice(0, 5).map(item => (
                      <div key={item.id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <Badge className={getFeedbackTypeColor(item.type)}>{item.type}</Badge>
                          {item.rating && <span className="text-yellow-500">{'⭐'.repeat(item.rating)}</span>}
                        </div>
                        <p className="text-sm text-gray-700 line-clamp-2">{item.message}</p>
                        <div className="flex items-center justify-between mt-2">
                          <p className="text-xs text-gray-500">{item.name || 'Anonymous'}</p>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => updateFeedbackStatus(item.id, 'reviewed')}
                          >
                            Mark Reviewed
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Unanswered Questions */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">❓ Unanswered Questions</CardTitle>
              </CardHeader>
              <CardContent>
                {questions.filter(q => !q.answered).length === 0 ? (
                  <p className="text-gray-500 text-center py-4">All questions answered! 🎉</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {questions.filter(q => !q.answered).slice(0, 6).map(q => (
                      <div key={q.id} className="p-4 bg-gray-50 rounded-lg">
                        <Badge variant="outline" className="mb-2">{q.topic || 'General'}</Badge>
                        <p className="text-gray-800 font-medium mb-2">{q.question}</p>
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-gray-500">
                            <p>{q.name || 'Anonymous'}</p>
                            {q.email && <p className="text-teal-600">{q.email}</p>}
                          </div>
                          <Button 
                            size="sm"
                            onClick={() => markQuestionAnswered(q.id)}
                          >
                            Mark Answered
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Sessions Tab */}
        <TabsContent value="sessions">
          <div className="mb-4 flex gap-4">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sessions</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Visitor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Messages</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredSessions.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">No sessions found</td>
                  </tr>
                ) : (
                  filteredSessions.map(session => (
                    <tr key={session.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{getLeadTypeIcon(session.lead_type)}</span>
                          <span className="font-medium">{session.visitor_name || 'Visitor'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          {session.visitor_email && <p className="text-gray-800">{session.visitor_email}</p>}
                          {session.visitor_phone && <p className="text-gray-500">{session.visitor_phone}</p>}
                          {!session.visitor_email && !session.visitor_phone && <p className="text-gray-400">No contact</p>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={getStatusColor(session.lead_type)}>{session.lead_type || 'browse'}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={getStatusColor(session.status)}>{session.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{session.source_page}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(session.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedSession(selectedSession?.id === session.id ? null : session)}
                        >
                          {session.messages?.length || 0} msgs
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Message Detail Panel */}
          {selectedSession && (
            <div className="mt-4 bg-white rounded-lg shadow p-4">
              <h3 className="font-bold text-gray-800 mb-4">Chat Messages - {selectedSession.visitor_name || 'Visitor'}</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {selectedSession.messages?.map((msg, i) => (
                  <div key={i} className={`p-3 rounded-lg ${msg.message_type === 'user' ? 'bg-blue-50 ml-8' : 'bg-gray-50 mr-8'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-xs">{msg.message_type}</Badge>
                      <span className="text-xs text-gray-400">{new Date(msg.created_at).toLocaleTimeString()}</span>
                    </div>
                    <p className="text-gray-800">{msg.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Feedback Tab */}
        <TabsContent value="feedback">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {feedback.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <Badge className={getFeedbackTypeColor(item.type)}>{item.type}</Badge>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className="text-gray-800 truncate">{item.message}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <p className="font-medium">{item.name || 'Anonymous'}</p>
                        {item.email && <p className="text-gray-500 text-xs">{item.email}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {item.rating ? <span className="text-yellow-500">{'⭐'.repeat(item.rating)}</span> : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={getStatusColor(item.status)}>{item.status}</Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(item.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Select 
                        value={item.status}
                        onValueChange={(v) => updateFeedbackStatus(item.id, v)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="reviewed">Reviewed</SelectItem>
                          <SelectItem value="responded">Responded</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Questions Tab */}
        <TabsContent value="questions">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Topic</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Question</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {questions.map(q => (
                  <tr key={q.id} className={`hover:bg-gray-50 ${!q.answered ? 'bg-yellow-50' : ''}`}>
                    <td className="px-4 py-3">
                      <Badge variant="outline">{q.topic || 'General'}</Badge>
                    </td>
                    <td className="px-4 py-3 max-w-md">
                      <p className="text-gray-800">{q.question}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <p className="font-medium">{q.name || 'Anonymous'}</p>
                        {q.email && <p className="text-teal-600 text-xs">{q.email}</p>}
                        {q.phone && <p className="text-gray-500 text-xs">{q.phone}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={q.answered ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                        {q.answered ? 'Answered' : 'Pending'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(q.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!q.answered && (
                        <Button size="sm" onClick={() => markQuestionAnswered(q.id)}>
                          Mark Answered
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ManageChatLogs;
